import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  generatePageSchema,
  schemaToJsonString,
} from "../utils/schemaGenerator";

/**
 * Custom hook to auto-generate schema for any page
 *
 * @param {Object} config - Page configuration
 * @param {string} config.type - Page type: 'WebPage', 'Product', 'CollectionPage', 'Service', 'FAQPage'
 * @param {string} config.title - Page title
 * @param {string} config.description - Page description
 * @param {string} config.image - Page image URL (optional)
 * @param {Array} config.breadcrumbs - Breadcrumb items [{name, url}]
 * @param {Object} config.product - Product data (for Product pages)
 * @param {string} config.categoryName - Category name (for CollectionPage)
 * @param {string} config.serviceType - Service type (for Service pages)
 * @param {Array} config.faqs - FAQ items [{question, answer}] (for FAQPage)
 *
 * @returns {string} JSON-LD schema as string
 */
export const usePageSchema = (config = {}) => {
  const location = useLocation();

  // Get base URL from window or use default
  const getBaseUrl = () => {
    if (typeof window !== "undefined") {
      return `${window.location.protocol}//${window.location.host}`;
    }
    return "https://primecloudsystems.com";
  };

  const baseUrl = config.baseUrl || getBaseUrl();

  const schema = useMemo(() => {
    const url = config.url || `${baseUrl}${location.pathname}`;

    const pageConfig = {
      ...config,
      url,
      baseUrl,
      // Auto-generate breadcrumbs if not provided
      breadcrumbs:
        config.breadcrumbs ||
        generateDefaultBreadcrumbs(location.pathname, baseUrl),
    };

    const generatedSchema = generatePageSchema(pageConfig);
    return schemaToJsonString(generatedSchema);
  }, [location.pathname, config, baseUrl]);

  return schema;
};

/**
 * Generate default breadcrumbs based on pathname
 */
const generateDefaultBreadcrumbs = (pathname, baseUrl) => {
  const breadcrumbs = [{ name: "Home", url: baseUrl }];

  const paths = pathname.split("/").filter(Boolean);

  let currentPath = "";
  paths.forEach((path, index) => {
    currentPath += `/${path}`;
    const name = path
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    breadcrumbs.push({
      name,
      url: `${baseUrl}${currentPath}`,
    });
  });

  return breadcrumbs;
};
